import { ChangeDetectorRef, Component, ViewChild, TemplateRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Table } from 'primeng/table';
import { DecimalPipe } from '@angular/common';
import { UiSwitchModule } from 'ngx-ui-switch';

import { BA_FiscalYearVm } from 'app/shared/generated/BA/Models/Admin/BA_FiscalYearVm';
import { SystemMessage, SystemMessageService } from 'app/core/system-message/system-message-service';
import { AuditHistoryService } from 'app/shared/audit-history/auditHistory.service';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { TableTopLegendVm } from 'app/shared/table/table-top/table-top-legend/table-top-legendVm';
import { BAService } from 'app/ba/ba.service';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { GlobalService } from 'app/shared/services/global.service';
import { AvailableColumns, VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableInput } from 'app/shared/standalone/shared-table/lazy-table-input.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';

@Component({
    selector: 'pcg-ba-fiscal-years-list'
    , templateUrl: './fiscal-years-list.component.html'
    , styleUrl: './fiscal-years-list.component.scss'
    , standalone: true
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
        , UiSwitchModule
    ]
})
export class FiscalYearsListComponent implements OnInit {
    
    baService = inject(BAService);
    toastr = inject(ToastrService);
    http = inject(HttpClient);
    auditHistoryService = inject(AuditHistoryService);
    modalService = inject(NgbModal);
    ms = inject(SystemMessageService);
    title = inject(Title);
    decimalPipe = inject(DecimalPipe);
    lts = inject(LazyTableService);
    cdr = inject(ChangeDetectorRef);

    @ViewChild('fiscalYearModal', { static: true }) fiscalYearModal: TemplateRef<any>;

    columnDefs = BA_FiscalYearVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    list: BA_FiscalYearVm[] = [];
    availableColumns!: AvailableColumns[];
    visibleColumns!: AvailableColumns[];
    searchColumns!: string[];

    isMobile = false;
    isLoading: boolean = true;
    searchValue: string | undefined;

    filters = { isActive: true };
    initFilters = Object.assign({}, this.filters);
    formGroup = BA_FiscalYearVm.Form;
    id: number;
    isActive: boolean;
    customExcelUrl = "api/BA/GetFiscalYearListExcel";

    // Color for table row highlighting and table legend
    isCurrentColor = "#c5ffc0";
    isDeactivatedColor = "#ff5c5c";
    // Table legend
	legend: TableTopLegendVm[] = [
		{
			id: 0
            , colorCode: this.isCurrentColor
            , description: 'The current fiscal year'
		}
        , {
            id: 1
            , colorCode: this.isDeactivatedColor
            , description: 'Fiscal years that are not active'
        }
	];

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    lazyTableInput: LazyTableInput = {
        pageNumber: 1
        , pageSize: 100
        , sortField: "endDate"
        , sortOrder: 1
    };

    constructor(title: Title) { title.setTitle("B&A Fiscal Years"); }

    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.getList();
    }

    getList() { 
        this.http.get(`api/BA/GetFiscalYearList` 
            + `?isActive=${this.filters.isActive}`
        ).subscribe((lst: BA_FiscalYearVm[]) => { 
            this.list = lst;
            this.availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
            this.visibleColumns = this.availableColumns.filter(o => o.isVisible);
            this.searchColumns = this.availableColumns.filter(o => o.canSearch).map(o => o.field);
            this.isLoading = false;
            this.setExcelUrl();
        }); 
    }

    columnChange(event: any) {
        this.visibleColumns = event;
        this.visibleColumns = event.sort((a, b) => {
            return this.availableColumns.findIndex(p => p.field === a.field) 
                - this.availableColumns.findIndex(p => p.field === b.field);
        });
    }

    clearFilters(
        table: Table
        , el: any
    ) {
        table.clear();
        this.searchValue = '';
        el.value = "";
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

	createFiscalYear() {
        this.id = 0;
        this.formGroup = BA_FiscalYearVm.Form;
        this.modalService.open(this.fiscalYearModal);
    }

    openModal(row: BA_FiscalYearVm) {
        this.id = row.fiscalYearId;
        this.getFiscalYear();
    }

    getFiscalYear() {
        this.http.get(`api/BA/GetFiscalYear?id=${this.id}`)
            .subscribe((vm: BA_FiscalYearVm) => {
                this.formGroup.patchValue(vm);
                this.isActive = vm.isActive;
                this.modalService.open(this.fiscalYearModal);
            }
        );
    }

    saveForm() {
        if (this.formGroup.valid) {
            this.http.post('api/BA/SaveFiscalYear', this.formGroup.getRawValue())
                .subscribe((msg: SystemMessage) => {
                    if (msg.isSuccessful) {
                        this.formGroup.patchValue(msg.model);
                        this.modalService.dismissAll();
                        this.getList();
                        this.toastr.success(`${msg.message}`, null, { positionClass: 'toast-top-right', timeOut: 2000 });
                    } else { this.toastr.error(`${msg.message}`, null, { positionClass: 'toast-top-right', timeOut: 2000 }); }
                }
            );
        } else { validateForm(this.formGroup); }
    }

    activateDeactivate() {
        this.http.post(`api/BA/ActivateDeactivateFiscalYear?id=${this.id}`, null)
            .subscribe((msg: SystemMessage) => {
                if (msg.isSuccessful) {
                    this.isActive = msg.model.isActive;
                    this.getList();
                } else { this.toastr.error(`${msg.message}`, null, { positionClass: 'toast-top-right', timeOut: 2000 }); }
            }
        );
    }

    setExcelUrl() { this.customExcelUrl = `api/BA/GetFiscalYearListExcel?isActive=${this.filters.isActive ?? ""}`; }

    getRowColor(row: BA_FiscalYearVm) {
        if (row.isCurrent) { return this.isCurrentColor; }
        if (!row.isActive) { return this.isDeactivatedColor; }
        return null;
    }
}
